import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import type { Page } from '@/api/types';
import { useSession } from '@/stores/session';
import { Tag } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Empty, Skeleton } from '@/components/States';
import { date, dateTime, money, num, qty } from '@/lib/format';
import { legacyHref } from '@/shell/nav';
import { DetailError, DetailLoading, DetailShell, KV, Kpis, Panel, detailStyles as d } from './kit/Detail';
import { imageSrc, productCover, Thumb } from './kit/Thumb';
import { useResource } from './kit/hooks';
import { availClass, defaultMark, imageType, quoteSource } from './status';
import { boxSize, packagingLabel, perPackage, stockTotals, withUnit } from './rules';
import type { PackagingOption, ProductDetail as TProduct, QuoteHistory } from './types';
import { PackagingSheet } from './PackagingSheet';
import { ProductStatusDialog } from './ProductStatusDialog';
import type { ProductStatusPreview } from './api';
import type { InventoryTransaction } from '../inventory/types';
import { transactionKind, signedQuantity } from '../inventory/status';
import { TxSource } from '../inventory/TxCards';
import s from './ProductDetail.module.css';

function Spec({ items }: { items: [string, string | null | undefined][] }) {
  const shown = items.filter(([, v]) => v);
  if (!shown.length) return null;
  return (
    <div className={s.spec}>
      {shown.map(([k, v]) => <div key={k}><span>{k}</span><b className="tnum">{v}</b></div>)}
    </div>
  );
}

function KindMark({ kind }: { kind: 'bottle' | 'box' }) {
  return <span className={`${s.kindMark} ${kind === 'box' ? s.kindBox : s.kindBottle}`}><Icon name={kind === 'box' ? 'carton' : 'bottle'} size={14} />{kind === 'box' ? '箱' : '瓶'}</span>;
}

function BottleSpecs({ items }: { items: [string, ReactNode][] }) {
  const shown = items
    .filter(([label, value]) => label !== '说明' || Boolean(value))
    .map(([label, value]) => [label, value ?? '—'] as [string, ReactNode]);
  if (!shown.length) return null;
  return <div className={s.bottleSpec}>{shown.map(([label, value]) => <div key={label} className={label === '说明' ? s.specDescription : undefined}><span>{label}</span><b className={value === '—' ? 'em tnum' : 'tnum'}>{value}</b></div>)}</div>;
}

/** 一个包装方案（整箱）：箱规、重量、体积、装柜量 + 这个方案下的供应商报价 */
function Packaging({ o, index, onEdit }: { o: PackagingOption; index: number; onEdit?: () => void }) {
  const quotes = o.supplierQuotes || [];
  return (
    <div className={s.pk}>
      <div className={s.pkh}>
        <b>{packagingLabel(o, index)}</b>
        {o.isDefault && <Tag tone={defaultMark.tone} sm>{defaultMark.label}</Tag>}
        {onEdit && <button type="button" className={s.packagingEdit} onClick={onEdit}>维护方案</button>}
        <span className="tnum">{perPackage(o) || <span className="em">未填每箱数量</span>}</span>
      </div>
      <Spec
        items={[
          ['包装方式', o.packageMethod],
          ['箱规（长×宽×高）', boxSize(o)],
          ['整箱毛重', withUnit(o.grossWeight, 'kg')],
          ['整箱净重', withUnit(o.netWeight, 'kg')],
          ['整箱体积', withUnit(o.volume, 'm³')],
          ["20' 装量", o.loadingQuantity20gp != null ? qty(o.loadingQuantity20gp) : null],
          ["40' 装量", o.loadingQuantity40gp != null ? qty(o.loadingQuantity40gp) : null],
          ["40'HQ 装量", o.loadingQuantity40hq != null ? qty(o.loadingQuantity40hq) : null],
        ]}
      />
      {(o.description || o.notes) && <p className={s.pkn}>{[o.description, o.notes].filter(Boolean).join('；')}</p>}
      {quotes.length ? (
        <table className={`${d.tbl} ${s.qt}`}>
          <thead><tr><th>供应商</th><th className={d.r}>报价</th><th>默认</th><th className={s.hideNarrow}>更新</th><th className={s.hideNarrow}>备注</th></tr></thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id}>
                <td>{q.supplier?.name || '未命名供应商'}</td>
                <td className={d.r}>{money(q.quotePrice)}{q.currency && q.currency !== 'CNY' ? ` ${q.currency}` : ''}</td>
                <td>{q.isDefault ? <Tag tone={defaultMark.tone} sm>{defaultMark.label}</Tag> : <span className="em">—</span>}</td>
                <td className={`tnum em ${s.hideNarrow}`}>{date(q.updatedAt)}</td>
                <td className={s.hideNarrow}>{q.note || <span className="em">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className={s.noq}>这个方案还没有供应商报价。</p>}
    </div>
  );
}

/** 报价历史：老详情页底部的表，点开才请求 */
function QuoteHistoryPanel({ productId }: { productId: string }) {
  const [open, setOpen] = useState(false);
  const path = `/api/supplier-quotes/history?productId=${encodeURIComponent(productId)}&pageSize=100`;
  const { data, loading, error, reload } = useResource<Page<QuoteHistory>>(open ? path : null);
  return (
    <Panel
      title="供应商报价历史"
      note="改价时旧价存档，采购单按下单当时的报价结算"
      extra={<Button size="sm" variant="text" onClick={() => setOpen((x) => !x)}>{open ? '收起' : '展开'}</Button>}
      flush
    >
      {!open ? null : error ? (
        <DetailError title="报价历史加载失败" error={error} path={path} onRetry={reload} />
      ) : loading || !data ? (
        <div className={s.pad}><Skeleton lines={3} /></div>
      ) : data.items.length ? (
        <table className={d.tbl}>
          <thead><tr><th>时间</th><th>包装方案</th><th>供应商</th><th className={d.r}>报价</th><th className={s.hideNarrow}>来源</th><th className={s.hideNarrow}>操作人</th></tr></thead>
          <tbody>
            {data.items.map((h) => (
              <tr key={h.id}>
                <td className="tnum">{dateTime(h.createdAt)}</td>
                <td>{h.packagingOption.packageName || h.packagingOption.packageMethod || h.packagingOption.description || '—'}</td>
                <td>{h.supplier.name}{h.isDefault && <small>默认报价</small>}</td>
                <td className={d.r}>{money(h.quotePrice)}</td>
                <td className={s.hideNarrow}>{quoteSource[h.source || ''] || h.source || '—'}</td>
                <td className={s.hideNarrow}>{h.createdBy?.name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className={d.none}>还没有报价记录。</p>}
    </Panel>
  );
}

function ProductRefsPanel({ productId }: { productId: string }) {
  const path = `/api/v2/products/${encodeURIComponent(productId)}/status`;
  const { data, loading, error, reload } = useResource<ProductStatusPreview>(path);
  return <Panel title="被谁用着">
    {error ? <div className={s.refsBody}><span role="alert">引用信息加载失败：{error.message}</span><button type="button" onClick={reload}>重试</button></div> : loading && !data ? <Skeleton lines={4} /> : data ? <div className={s.refsBody}>
      <KV rows={[
        ['套装', `${data.bundleCount} 个${data.bundles.length ? ` · ${data.bundles.map((item) => item.code).join('、')}` : ''}`],
        ['未完成订单', `${data.orderCount} 张`],
        ['未完成采购', `${data.purchaseCount} 张`],
        ['库存仓库', `${data.stocks.length} 个${data.stocks.length ? ` · ${data.stocks.map((item) => `${item.warehouse.name} ${qty(item.currentQuantity)}`).join('、')}` : ''}`],
      ]} />
      <p>有引用的产品不能删除，可停用并保留历史。</p>
    </div> : null}
  </Panel>;
}

function RecentTransactionsPanel({ productId }: { productId: string }) {
  const can = useSession((state) => state.can);
  const allowed = can('inventory.transactions.read');
  const path = `/api/v2/inventory/transactions?productId=${encodeURIComponent(productId)}&page=1&pageSize=3`;
  const { data, loading, error, reload } = useResource<Page<InventoryTransaction>>(allowed ? path : null);
  if (!allowed) return null;
  return <Panel title="近期流水" extra={<Link className={s.link} to={`/inventory/transactions?productId=${encodeURIComponent(productId)}`}>全部</Link>} flush>
    {error ? <div className={s.recentError} role="alert">加载失败：{error.message}<button type="button" onClick={reload}>重试</button></div> : loading && !data ? <Skeleton lines={3} /> : data?.items.length ? <ul className={s.recentList}>
      {data.items.map((item) => {
        const kind = transactionKind(item);
        const amount = signedQuantity(item);
        return <li key={item.id}><div className={s.recentTop}><Tag tone={kind.tone} sm>{kind.label}</Tag><b className={`tnum ${amount.tone === 'in' ? s.in : amount.tone === 'out' ? s.out : ''}`}>{amount.sign}{qty(amount.value)}</b></div><div className={s.recentMeta}><time>{dateTime(item.createdAt)}</time>{item.sourceOrder || item.sourcePurchaseOrder || item.sourceShipment || item.sourceTaobaoOrder ? <> · <TxSource t={item} /></> : null}</div></li>;
      })}
    </ul> : <p className={s.muted}>暂无库存流水。</p>}
  </Panel>;
}

export function ProductDetail({ id, embedded, openPackaging = false }: { id: string; embedded?: boolean; openPackaging?: boolean }) {
  const formFactor = useFormFactor();
  const phone = formFactor === 'phone';
  const can = useSession((st) => st.can);
  const [searchParams, setSearchParams] = useSearchParams();
  const [packagingOpen, setPackagingOpen] = useState<string | null>(null);
  const statusRequested = searchParams.get('status') === '1';
  const [statusOpen, setStatusOpen] = useState(statusRequested);
  const openedFromList = useRef(false);
  const path = `/api/products/${id}`;
  const { data: p, loading, error, reload } = useResource<TProduct | null>(path);
  useEffect(() => {
    if (statusRequested) setStatusOpen(true);
  }, [statusRequested]);
  const closeStatus = () => {
    setStatusOpen(false);
    if (searchParams.has('status')) {
      const next = new URLSearchParams(searchParams);
      next.delete('status');
      setSearchParams(next, { replace: true });
    }
  };
  useEffect(() => {
    if (!openPackaging || openedFromList.current || !p) return;
    openedFromList.current = true;
    setPackagingOpen(p.packagingOptions[0]?.id || 'new');
  }, [openPackaging, p]);

  if (error) return <DetailError title="产品加载失败" error={error} path={path} onRetry={reload} />;
  if (loading || p === undefined) return <DetailLoading />;
  if (p === null) {
    return <Empty icon="info" title="产品不存在" action={<Link className={s.back} to="/products">回产品列表</Link>}>可能已被删除，或者链接有误。</Empty>;
  }

  const t = stockTotals(p.stocks);
  const costQuantity = p.stocks.reduce((total, stock) => total + (stock.inventoryUnitCost === undefined ? 0 : num(stock.currentQuantity)), 0);
  const averageInboundCost = costQuantity > 0
    ? p.stocks.reduce((total, stock) => total + (stock.inventoryUnitCost === undefined ? 0 : num(stock.currentQuantity) * stock.inventoryUnitCost), 0) / costQuantity
    : undefined;
  const cover = productCover(p);
  const images = p.images.length ? p.images : cover ? [{ id: 'cover', url: cover, type: 'COVER' as const }] : [];
  const drawing = imageSrc(p.drawingFileUrl);

  const media = (
    <Panel title="图片与图纸" note="主图进列表和画册">
      {images.length ? (
        <div className={s.imgs}>
          {images.map((im) => (
            <a key={im.id} href={imageSrc(im.url)} target="_blank" rel="noreferrer" className={s.im}>
              <Thumb src={imageSrc(im.thumbnailUrl || im.url)} alt={im.alt || p.name} square />
              <small>{imageType[im.type] || '图片'}</small>
            </a>
          ))}
        </div>
      ) : <p className={s.muted}>还没有图片。</p>}
      <div className={s.draw}>
        <Icon name="file" />
        {drawing ? <a href={drawing} target="_blank" rel="noreferrer">{p.drawingFileName || '图纸文件'}</a> : <span className="em">没有图纸</span>}
      </div>
    </Panel>
  );

  const warehousePanel = (
    <Panel title="各仓库存" note={phone ? `入库均价 ${averageInboundCost === undefined ? '—' : money(averageInboundCost)}` : '可用 = 现有 − 预占，可以为负'} extra={!phone ? <a className={s.link} href={legacyHref(`/inventory?productId=${p.id}`)}>库存流水（老版）</a> : undefined} flush>
      {p.stocks.length ? (
        phone ? <div className={s.phoneStocks}>{p.stocks.map((st) => { const available = num(st.currentQuantity) - num(st.reservedQuantity); return <article key={st.id}><div className={s.phoneStockHead}><b>{st.warehouse.code} · {st.warehouse.name}</b><span className={availClass(available)}><strong>{qty(available)}</strong><small>可用</small></span></div><div className={s.phoneStockFacts}><span>现有 <b>{qty(st.currentQuantity)}</b></span><span>预占 <b>{qty(st.reservedQuantity)}</b></span><span>价值 <b>{st.inventoryValue !== undefined ? money(st.inventoryValue) : '—'}</b></span></div></article>; })}</div> :
        <table className={d.tbl}>
          <thead><tr><th>仓库</th><th className={d.r}>现有</th><th className={d.r}>预占</th><th className={d.r}>可用</th><th className={d.r}>库存价值</th></tr></thead>
          <tbody>
            {p.stocks.map((st) => {
              const a = num(st.currentQuantity) - num(st.reservedQuantity);
              return (
                <tr key={st.id}>
                  <td>{st.warehouse.name}<small>{st.warehouse.code}</small></td>
                  <td className={d.r}>{qty(st.currentQuantity)}</td>
                  <td className={d.r}>{qty(st.reservedQuantity)}</td>
                  <td className={`${d.r} ${availClass(a)}`}><b>{qty(a)}</b></td>
                  <td className={d.r}>{st.inventoryValue !== undefined ? money(st.inventoryValue) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <p className={d.none}>还没有库存记录。</p>}
    </Panel>
  );

  const specsPanel = (
    <Panel title="产品规格" mark={phone && <KindMark kind="bottle" />} note="瓶子本身的规格">
      <BottleSpecs items={[
        ['瓶重', p.bottleWeight == null ? null : `${num(p.bottleWeight)} g`],
        ['瓶高', p.bottleHeight == null ? null : `${num(p.bottleHeight)} mm`],
        ['瓶身直径', p.bottleDiameter == null ? null : `${num(p.bottleDiameter)} mm`],
        ['料色', p.materialColor], ['口类型', p.mouthType], ['配套盖子', p.matchingCap],
        ['说明', p.description ? <span className={s.pre}>{p.description}</span> : null],
      ]} />
    </Panel>
  );

  const packagingPanel = (
    <Panel
      title="包装方案与供应商报价"
      mark={phone && <KindMark kind="box" />}
      note="整箱规格：长宽高、毛净重、体积都是一整箱的"
      extra={can('products.write') ? <><button type="button" className={s.link} onClick={() => setPackagingOpen('new')}>新增方案</button>{p.packagingOptions.length > 0 && <button type="button" className={s.link} onClick={() => setPackagingOpen(p.packagingOptions[0].id)}>维护</button>}</> : undefined}
      flush
    >
      {p.packagingOptions.length ? p.packagingOptions.map((o, i) => <Packaging key={o.id} o={o} index={i} onEdit={can('products.write') ? () => setPackagingOpen(o.id) : undefined} />) : <p className={d.none}>还没有包装方案。采购按包装方案下单，请先新增。</p>}
    </Panel>
  );

  const main = (
    <>
      {phone && <div className={s.phoneIdentity}><div><h2>{p.name}</h2><Tag tone={t.available < 0 ? 'red' : 'none'} sm>可用 {qty(t.available)}</Tag></div><p>{p.code} · {p.category || '未分类'} · 参考价 {money(p.salePrice)}</p><small>更新于 {date(p.updatedAt)}</small></div>}
      {!p.isActive && <p className={s.gone}>这个产品已停用：不会出现在新订单的产品选择中，历史库存流水和订单记录保留。</p>}
      <Kpis className={phone ? s.phoneKpis : undefined}
        items={[
          { label: '现有', value: qty(t.current) },
          { label: '预占', value: qty(t.reserved) },
          { label: '可用', value: qty(t.available), tone: t.available < 0 ? 'neg' : undefined },
          ...(!phone && averageInboundCost !== undefined ? [{ label: '入库均价', value: money(averageInboundCost) }] : []),
          ...(t.value !== undefined ? [{ label: '库存价值', value: money(t.value) }] : []),
        ]}
      />

      {formFactor === 'tablet' && !embedded ? <>{specsPanel}{packagingPanel}{warehousePanel}</> : <>{warehousePanel}{specsPanel}{packagingPanel}</>}

      <QuoteHistoryPanel productId={p.id} />
      {media}
    </>
  );

  return <>
    <DetailShell
      embedded={embedded}
      backTo="/products"
      title={phone ? '产品详情' : p.name}
      badges={!phone && (
        <>
          {!p.isActive && <Tag tone="none">已停用</Tag>}
          <span className={s.avail}>可用 <b className={`tnum ${availClass(t.available)}`}>{qty(t.available)}</b></span>
        </>
      )}
      openHref={`/products/${p.id}`}
      actions={!phone && <>{can('products.write') && <><Link className={s.link} to={`/products/${p.id}/copy`}>复制产品</Link><button type="button" className={s.link} onClick={() => setPackagingOpen(p.packagingOptions[0]?.id || 'new')}>包装方案与报价</button><Link className={s.link} to={`/products/${p.id}/edit`}>编辑</Link>{formFactor === 'tablet' && <button type="button" className={s.tabletMore} aria-label="更多产品操作" onClick={() => setStatusOpen(true)}><Icon name="more" size={20} /></button>}</>}</>}
      footer={phone && can('products.write') ? <footer className={s.phoneActions}><button type="button" aria-label="更多产品操作" onClick={() => setStatusOpen(true)}><Icon name="more" size={20} /></button><button type="button" onClick={() => setPackagingOpen(p.packagingOptions[0]?.id || 'new')}>包装方案</button><Link to={`/products/${p.id}/edit`}>编辑</Link></footer> : undefined}
      sub={!phone && <>{p.code} · {p.category || '未分类'} · 销售参考价 {money(p.salePrice)} · 更新于 {date(p.updatedAt)}</>}
      main={main}
      aside={<><ProductRefsPanel productId={p.id} /><RecentTransactionsPanel productId={p.id} /></>}
    />
    {packagingOpen && <PackagingSheet key={packagingOpen} productId={p.id} productName={p.name} option={p.packagingOptions.find((option) => option.id === packagingOpen)} onClose={() => setPackagingOpen(null)} onChanged={reload} />}
    {statusOpen && <ProductStatusDialog id={p.id} onClose={closeStatus} onChanged={reload} />}
  </>;
}
